import React from "react";
import { Button, Divider, Modal, Tag, Typography } from "antd";
import useAccess from "../../Hooks/useAccess";
import { formatPlanName } from "../../Utils/planUtils";
import { WarningOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../Context/AppContext/AppContext";
import "./index.scss";

interface SubscriptionWarningProps {
  open: boolean;
  handleClose: () => void;
  title: string;
  type:
    | "job-post-limit"
    | "job-application-limit"
    | "team-member-limit"
    | "org-logo"
    | "custom-theme"
    | "custom-domain";
}

const SubscriptionWarning: React.FC<SubscriptionWarningProps> = ({
  open,
  handleClose,
  title,
  type,
}) => {
  const navigate = useNavigate();
  const { addons } = useAppContext();
  const { currentPlan, currentSubscription, currentLimits, currentMode } =
    useAccess();

  if (!currentPlan || !currentSubscription || !currentLimits || !currentMode) {
    return null;
  }

  const getModeText = () => {
    if (currentMode === "free") {
      return "Free Mode";
    } else if (currentMode === "credit") {
      return "Credit Mode";
    } else if (currentMode === "base") {
      return "Base Mode";
    }
    return "Unknown Mode";
  };

  const getMessageByType = () => {
    const planName = formatPlanName(currentPlan.name);
    switch (type) {
      case "job-post-limit":
        const allowedJobPosts = currentLimits.jobPosts;
        return `Your current subscription plan allows only ${allowedJobPosts} active job post${
          allowedJobPosts === 1 ? "" : "s"
        }. Please upgrade to create more.`;

      case "job-application-limit":
        const allowedJobApplications = currentLimits.applicationsProcessed;
        const usedJobApplications =
          currentSubscription.measurements.applicationsProcessed;

        return (
          <>
            Your current plan <strong>({planName})</strong> allows{" "}
            <strong>{allowedJobApplications}</strong> assessment
            {allowedJobApplications === 1 ? "" : "s"}. You’ve already used{" "}
            <strong>{usedJobApplications}</strong>. To{" "}
            <strong>process more assessments</strong>, please{" "}
            <strong>upgrade your plan</strong> or{" "}
            <strong>inactivate existing ones</strong>.
          </>
        );
      case "team-member-limit":
        return `Your current subscription plan allows only ${currentLimits?.teamMembers} active team members, Please upgrade your plan.`;
      case "org-logo":
        return "Your current plan does not allow you to upload company logo. Please upgrade your plan.";
      case "custom-theme":
        return "Your current plan does not allow you to customize your theme. Please upgrade your plan.";
      case "custom-domain":
        return "Your current plan does not allow you to set a custom domain. Please upgrade your plan";
      default:
        return "Unknown type";
    }
  };

  const getProcessedByType = () => {
    switch (type) {
      case "job-post-limit":
        return currentSubscription.measurements.jobPosts;
      case "job-application-limit":
        return currentSubscription.measurements.applicationsProcessed;
      case "team-member-limit":
        return currentSubscription.measurements.teamMembers;
      default:
        return 0;
    }
  };

  const getLimitByType = () => {
    switch (type) {
      case "job-post-limit":
        return currentLimits.jobPosts;
      case "job-application-limit":
        return currentLimits.applicationsProcessed;
      case "team-member-limit":
        return currentLimits.teamMembers;
      default:
        return 0;
    }
  };

  const getFeatureByType = () => {
    switch (type) {
      case "job-post-limit":
        return "Job Posts";
      case "job-application-limit":
        return "Assessments";
      case "team-member-limit":
        return "Team Members";
      default:
        return "Unknown Feature";
    }
  };

  const getUpgradeButtonText = () => {
    switch (type) {
      case "job-post-limit":
        if (currentMode === "free") {
          return "Upgrade Plan";
        } else if (currentMode === "credit") {
          return "Buy More Credits";
        } else {
          return "Buy Job Post Add-on";
        }
      case "job-application-limit":
        if (currentMode === "free") {
          return "Upgrade Plan";
        } else if (currentMode === "credit") {
          return "Buy More Credits";
        } else {
          return "Buy Assessment Add-on";
        }
      case "team-member-limit":
        return "Upgrade Plan";
      case "org-logo":
        return "Upgrade Plan";
      case "custom-theme":
        return "Upgrade Plan";
      case "custom-domain":
        return "Upgrade Plan";
      default:
        return "Upgrade Plan";
    }
  };

  const handleUpgrade = () => {
    const mode = currentMode;
    if (mode === "free") {
      navigate("/pricing");
      return;
    } else if (mode === "credit") {
      navigate("/credit-purchase");
      return;
    } else {
      if (currentSubscription.status === "inactive") {
        navigate("/pricing");
        return;
      }

      switch (type) {
        case "job-application-limit":
          const assessmentAddon = addons.find(
            (addon) => addon.name === "Assessment Add-on",
          );
          if (assessmentAddon) {
            navigate(
              `/plan-purchase?action=addon&addonId=${assessmentAddon._id}`,
            );
            return;
          } else {
            navigate("/pricing");
            return;
          }
        case "job-post-limit":
          const jobPostAddon = addons.find(
            (addon) => addon.name === "Job Posts Add-on",
          );
          if (jobPostAddon) {
            navigate(`/plan-purchase?action=addon&addonId=${jobPostAddon._id}`);
            return;
          } else {
            navigate("/pricing");
            return;
          }
        default:
          navigate("/pricing");
          return;
      }
    }
  };

  return (
    <section className="subscription-warning-container">
      <Modal
        open={open}
        maskClosable={false}
        closeIcon={null}
        closable={false}
        footer={null}
        keyboard={false}
        centered
        className="subscription-warning-modal"
      >
        <section className="subscription-warning-content">
          <div className="subscription-warning-header">
            <Typography.Title level={5} style={{ margin: 0 }}>
              <WarningOutlined style={{ color: "#ff4d4f", fontSize: 20 }} />{" "}
              {title}
            </Typography.Title>
            <Tag>{getModeText()}</Tag>
          </div>

          <Divider style={{ margin: "0" }} />

          <Typography.Text>{getMessageByType()}</Typography.Text>
          {type !== "custom-domain" &&
            type !== "custom-theme" &&
            type !== "org-logo" && (
              <Typography.Text>
                (
                <Typography.Text type="danger">
                  {getProcessedByType()}
                </Typography.Text>
                /<Typography.Text strong>{getLimitByType()}</Typography.Text>){" "}
                {getFeatureByType()}
              </Typography.Text>
            )}

          <Divider style={{ margin: "0" }} />

          <div className="subscription-warning-buttons">
            <Button type="default" onClick={handleClose}>
              Close
            </Button>
            <Button type="primary" onClick={handleUpgrade}>
              {getUpgradeButtonText()}
            </Button>
          </div>
        </section>
      </Modal>
    </section>
  );
};

export default SubscriptionWarning;
